import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { menuItemSx } from '@backrs_ui/common-ui/src/molecules/Profile/styles/autocomplete.styles';

import {
  TextField,
  Autocomplete,
  AutocompleteProps,
  SxProps,
  Theme,
  Paper,
  Grow,
  Popper,
  PopperProps,
  Box,
  AutocompleteRenderOptionState,
} from '@mui/material';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';

type Option = Readonly<{
  label: string;
  value: string;
}>;

type InviteAutocompleteInputProps = Readonly<{
  name: string;
  placeholder?: string;
  rules?: RegisterOptions;
}> &
  Omit<AutocompleteProps<Option, false, true, false>, 'renderInput'>;

export const InviteAutocompleteInput = ({
  name,
  rules,
  ...otherProps
}: InviteAutocompleteInputProps) => {
  const methods = useFormContext();

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: { label: string; value: string },
    state: AutocompleteRenderOptionState,
  ) => {
    const { selected } = state;
    const { onClick, ...otherProps } = props;

    return (
      <Box {...otherProps} component="li" key={option.value} sx={menuItemSx} onClick={onClick}>
        <Box width="100%">{option.label}</Box>
        {selected && (
          <AppIcon iconName="Check" diameter="20px" color="Colors/Foreground/fg-brand-primary" />
        )}
      </Box>
    );
  };

  const CustomPopper = (props: PopperProps) => {
    const { children, ...otherProps } = props;

    return (
      <Popper {...otherProps} sx={popperSx} placement="bottom-end" transition>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top right' }} timeout={200}>
            <Box>{children as React.ReactElement}</Box>
          </Grow>
        )}
      </Popper>
    );
  };

  return (
    <Controller
      {...methods}
      name={name}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        return (
          <Autocomplete
            {...field}
            {...otherProps}
            onChange={(_, newValue) => {
              field.onChange(newValue?.value || '');
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disableClearable
            fullWidth
            sx={roleSelectContainerSx}
            disablePortal
            defaultValue={otherProps.defaultValue}
            PaperComponent={(props) => <Paper {...props} sx={paperSx} />}
            PopperComponent={CustomPopper}
            popupIcon={
              <AppIcon
                iconName="ChevronDown"
                diameter="20px"
                color="Colors/Foreground/fg-quaternary"
              />
            }
            renderOption={renderOption}
            renderInput={(params) => (
              <TextField
                {...params}
                id="family-invite-role-input"
                placeholder={otherProps.placeholder}
                error={!!error}
                helperText={error ? (error?.message as unknown as string) : ''}
                inputProps={{
                  ...params.inputProps,
                  readOnly: true,
                  'aria-label': 'Role select',
                }}
              />
            )}
          />
        );
      }}
    />
  );
};

const roleSelectContainerSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiAutocomplete-inputRoot': {
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    '&:hover': {
      border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
      boxShadow: 'none',
    },
    '&.Mui-focused': {
      borderColor: backrs.colors['Colors/Border/border-brand'],
      boxShadow: `0 0 0 1px ${backrs.colors['Colors/Border/border-brand']}`,
    },
  },
  '& .MuiAutocomplete-popupIndicator': {
    transition: 'transform 0.3s ease',
  },
});

const popperSx: SxProps<Theme> = {
  minWidth: '180px',
};

const paperSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-md'],
  minWidth: '180px',
});
